/**
 * TasksHub — the user's personal "Görevlerim" surface.
 *
 * Reached from the Dashboard KPI cards (`Toplam / Açık / Gecikmiş /
 * Bu hafta bitti`) via `#filter=<key>`. Renders a flat, chronologically
 * grouped list of the CURRENT USER's tasks across every project so they
 * can eyeball their pipeline without hopping project-to-project.
 *
 * Grouping = date bucket (Gecikmiş → Bugün → Bu hafta → Sonra → Tarihsiz).
 * Within each bucket rows are sorted by (project name, list name, due date).
 * Clicking a row opens the shared TaskRightPanel.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useTasksStore } from "../store/tasksStore";
import { dateGroupOf, dueBadge } from "../lib/dates";
import { getListPalette } from "../constants/listPalette";
import TaskRightPanel from "../components/tasks/TaskRightPanel";
import TaskDialog from "../components/tasks/TaskDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { canEditProtectedTaskFields, isAssignedToUser } from "../lib/taskPermissions";
import { LockedTaskField } from "../components/tasks/LockedTaskField";
import { ChevronLeft, Clock, AlertTriangle, TrendingUp, FolderKanban, ChevronDown, ChevronRight, User2, Plus, Check, Search, X, ListFilter, Layers, Lock } from "lucide-react";
import { useLabelsStore } from "../store/labelsStore";

const BUCKET_ORDER = ["overdue", "today", "this_week", "later", "no_date"];
const BUCKET_LABELS = {
  overdue: "Gecikmiş",
  today: "Bugün",
  this_week: "Bu hafta",
  later: "Sonra",
  no_date: "Tarihsiz",
};
const BUCKET_TONE = {
  overdue: "border-red-200 bg-red-50 text-red-800",
  today: "border-amber-200 bg-amber-50 text-amber-800",
  this_week: "border-sky-200 bg-sky-50 text-sky-800",
  later: "border-zinc-200 bg-zinc-50 text-zinc-700",
  no_date: "border-zinc-200 bg-white text-zinc-500",
};

const FILTERS = [
  {
    key: "all",
    label: "Tümü",
    icon: FolderKanban,
    match: () => true,
  },
  {
    key: "open",
    label: "Açık",
    icon: Clock,
    match: (t) => t.status !== "done",
  },
  {
    key: "overdue",
    label: "Gecikmiş",
    icon: AlertTriangle,
    match: (t) =>
      t.status !== "done" &&
      t.due_at &&
      dayjs(t.due_at).isBefore(dayjs()),
  },
  {
    key: "done_this_week",
    label: "Bu hafta bitti",
    icon: TrendingUp,
    match: (t) => {
      if (t.status !== "done") return false;
      const wStart = dayjs().startOf("week");
      const wEnd = dayjs().endOf("week");
      const done = t.done_at || t.updated_at;
      if (!done) return false;
      const d = dayjs(done);
      return d.isAfter(wStart) && d.isBefore(wEnd);
    },
  },
];

const PRIORITY_TONE = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-zinc-100 text-zinc-700",
  low: "bg-emerald-100 text-emerald-700",
};

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

export default function TasksHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant, user: currentUser } = useAuthStore();
  const { updateTask, deleteTask } = useTasksStore();

  // Always personal — this page is the user's own task inbox ("Görevlerim").

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lists, setLists] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelTask, setPanelTask] = useState(null);
  const [dialogTask, setDialogTask] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  // Tracks tasks whose checkbox is mid-flight so we can dim the row while
  // the PATCH resolves — prevents accidental double-clicks.
  const [togglingIds, setTogglingIds] = useState(new Set());

  // Free-text search — matches the task title (case + diacritic insensitive)
  // and the assigned labels' names so users can find rows via "acil".
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // Project & Group (list) filters — multi-select. Empty array = "all".
  // Applied AFTER the visibility scope + status filter chip so they narrow
  // the view further. Initial state seeded from `?project=` / `?list=`
  // query params (comma-separated) so bookmarked/shared URLs restore the
  // exact filtered view. Synced back to the URL on every change.
  const parseCsvParam = (val) =>
    (val || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [projectFilter, setProjectFilter] = useState(() =>
    parseCsvParam(searchParams.get("project")),
  );
  const [listFilter, setListFilter] = useState(() =>
    parseCsvParam(searchParams.get("list")),
  );

  // Inline "hızlı görev ekle" composer state — persists project/list across
  // submits so power users can slam in multiple tasks without re-picking.
  const [composerOpen, setComposerOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newProjectId, setNewProjectId] = useState("");
  const [newListId, setNewListId] = useState("");
  const [newDueAt, setNewDueAt] = useState("");
  const [creating, setCreating] = useState(false);

  // Read `#filter=<key>` from the URL hash.
  const activeFilterKey = useMemo(() => {
    const m = (location.hash || "").match(/filter=([a-z_]+)/i);
    return m && FILTERS.some((f) => f.key === m[1]) ? m[1] : "all";
  }, [location.hash]);

  const setFilter = (key) => {
    // Preserve current query string (project/list filters) when we
    // change the status chip — otherwise navigate() with a plain string
    // would wipe the ?project=…&list=… params from the URL.
    const qs = searchParams.toString();
    navigate(`/app/tasks${qs ? `?${qs}` : ""}#filter=${key}`, { replace: true });
  };

  // Sync `projectFilter` / `listFilter` state → URL query params so the
  // filtered view is bookmarkable + shareable. Uses `replace` to avoid
  // stuffing the browser history with every checkbox tick.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (projectFilter.length > 0) {
      next.set("project", projectFilter.join(","));
    } else {
      next.delete("project");
    }
    if (listFilter.length > 0) {
      next.set("list", listFilter.join(","));
    } else {
      next.delete("list");
    }
    // Only call setSearchParams if the serialized string actually changed
    // to avoid infinite re-render loops.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [projectFilter, listFilter]);

  // Cmd/Ctrl+K to focus the search box — feels native for a task hub.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Lowercase-fold a string for the diacritic-insensitive search compare. */
  const foldStr = (s) =>
    (s || "")
      .toString()
      .toLocaleLowerCase("tr")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const trimmedQuery = searchQuery.trim();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [tRes, pRes, lRes, uRes] = await Promise.all([
          api.get("/tasks", {
            params: { only_mine: true },
          }),
          api.get("/projects"),
          // The `/lists` endpoint requires a project_id in this codebase, so
          // we can't fetch all lists in a single call. Fetch per-project in
          // parallel below (after projects arrive).
          Promise.resolve({ data: [] }),
          api.get("/users"),
        ]);
        if (!alive) return;
        setTasks(tRes.data || []);
        setProjects(pRes.data || []);
        setUsers(uRes.data || []);
        // Fan-out to fetch lists per project
        const listArrays = await Promise.all(
          (pRes.data || []).map((p) =>
            api
              .get("/lists", { params: { project_id: p.id } })
              .then((r) => r.data || [])
              .catch(() => []),
          ),
        );
        if (!alive) return;
        setLists(listArrays.flat());
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "Görevler yüklenemedi",
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const projectMap = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p])),
    [projects],
  );
  const listMap = useMemo(
    () => Object.fromEntries(lists.map((l) => [l.id, l])),
    [lists],
  );
  const userMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users],
  );

  // Labels are tenant-wide, fetched once. `labelMap` powers the row chip
  // rendering and the filter dropdown without repeated array scans.
  const { labels, fetchLabels } = useLabelsStore();
  useEffect(() => {
    fetchLabels().catch(() => {});
  }, [fetchLabels]);
  const labelMap = useMemo(
    () => Object.fromEntries((labels || []).map((l) => [l.id, l])),
    [labels],
  );

  // Lists filtered by the currently-selected composer project. Recomputed
  // whenever the user switches project so the "Grup" dropdown never shows
  // groups from another project.
  const listsForNewProject = useMemo(
    () => lists.filter((l) => l.project_id === newProjectId),
    [lists, newProjectId],
  );

  // Once projects + lists arrive, pre-seed the composer with the first
  // project / first list so opening the composer is a single-click affair.
  useEffect(() => {
    if (!newProjectId && projects.length > 0) {
      setNewProjectId(projects[0].id);
    }
  }, [projects, newProjectId]);

  useEffect(() => {
    if (!newProjectId) {
      setNewListId("");
      return;
    }
    const options = lists.filter((l) => l.project_id === newProjectId);
    // If current list doesn't belong to selected project, reset to first option.
    if (!options.find((l) => l.id === newListId)) {
      setNewListId(options[0]?.id || "");
    }
  }, [newProjectId, lists, newListId]);

  const createInlineTask = async () => {
    const title = newTitle.trim();
    if (!title) {
      toast.error("Görev başlığı boş olamaz");
      return;
    }
    if (!newProjectId || !newListId) {
      toast.error("Proje ve grup seçmelisin");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        project_id: newProjectId,
        list_id: newListId,
        title,
      };
      if (newDueAt) {
        // <input type="date"> returns "YYYY-MM-DD" — normalize to full ISO.
        payload.due_at = new Date(`${newDueAt}T09:00:00`).toISOString();
      }
      const { data } = await api.post("/tasks", payload);
      setTasks((prev) => [data, ...prev]);
      toast.success(`"${title}" eklendi`);
      // Clear only the title/date — keep project/list for rapid entry.
      setNewTitle("");
      setNewDueAt("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Görev oluşturulamadı",
      );
    } finally {
      setCreating(false);
    }
  };

  const activeFilter = FILTERS.find((f) => f.key === activeFilterKey) || FILTERS[0];

  /**
   * Combined predicate: filter chip + free-text search. Applied both to
   * the bucket rows AND to the counts shown on the filter chips so the
   * badges reflect *what the user is actually seeing*.
   */
  const searchMatches = useMemo(() => {
    if (!trimmedQuery) return () => true;
    const q = foldStr(trimmedQuery);
    return (t) => {
      if (foldStr(t.title).includes(q)) return true;
      if (foldStr(t.description).includes(q)) return true;
      // Match on label names too so "acil" surfaces every tagged row.
      if (t.label_ids && t.label_ids.length) {
        for (const lid of t.label_ids) {
          const lbl = labelMap[lid];
          if (lbl && foldStr(lbl.name).includes(q)) return true;
        }
      }
      return false;
    };
  }, [trimmedQuery, labelMap]);

  /**
   * Compose project + group filter predicate. Both empty = match-all;
   * when at least one is selected we intersect (task's project ∈ set AND
   * task's list ∈ set). List filter is auto-scoped to the picked
   * projects so users can't get "invisible" empty results.
   */
  const projectListMatches = useMemo(() => {
    if (projectFilter.length === 0 && listFilter.length === 0) {
      return () => true;
    }
    const projectSet = projectFilter.length ? new Set(projectFilter) : null;
    const listSet = listFilter.length ? new Set(listFilter) : null;
    return (t) => {
      if (projectSet && !projectSet.has(t.project_id)) return false;
      if (listSet && !listSet.has(t.list_id)) return false;
      return true;
    };
  }, [projectFilter, listFilter]);

  const buckets = useMemo(() => {
    const out = {};
    for (const status of BUCKET_ORDER) out[status] = [];
    tasks
      .filter(activeFilter.match)
      .filter(projectListMatches)
      .filter(searchMatches)
      .forEach((t) => {
        const bucket = dateGroupOf(t.due_at, tenant?.timezone, t.status);
        if (!out[bucket]) out[bucket] = [];
        out[bucket].push(t);
      });
    // Sort within each bucket by (project name, list name, due date asc).
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => {
        const pA = projectMap[a.project_id]?.name || "";
        const pB = projectMap[b.project_id]?.name || "";
        if (pA !== pB) return pA.localeCompare(pB, "tr");
        const lA = listMap[a.list_id]?.name || "";
        const lB = listMap[b.list_id]?.name || "";
        if (lA !== lB) return lA.localeCompare(lB, "tr");
        const dA = a.due_at || "9999";
        const dB = b.due_at || "9999";
        return dA.localeCompare(dB);
      });
    }
    return out;
  }, [tasks, activeFilter, projectListMatches, searchMatches, tenant?.timezone, projectMap, listMap]);

  const totalVisible = Object.values(buckets).reduce((a, arr) => a + arr.length, 0);

  const toggleBucket = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openTask = (task) => setPanelTask(task);

  const handleUpdate = async (id, patch) => {
    await updateTask(id, patch);
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const handleDelete = async (id) => {
    await deleteTask(id);
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  /**
   * Flip a task between `done` and `todo` inline from the row checkbox.
   * Optimistic — we mark the row as toggling so consecutive clicks are
   * ignored while the PATCH is in flight.
   */
  const handleToggleDone = async (task) => {
    if (togglingIds.has(task.id)) return;
    setTogglingIds((prev) => {
      const next = new Set(prev);
      next.add(task.id);
      return next;
    });
    const nextStatus = task.status === "done" ? "todo" : "done";
    try {
      await updateTask(task.id, { status: nextStatus });
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)),
      );
      // Keep the right panel in sync when it's showing the same task.
      setPanelTask((prev) =>
        prev && prev.id === task.id ? { ...prev, status: nextStatus } : prev,
      );
      if (nextStatus === "done") {
        toast.success(`"${task.title}" tamamlandı`);
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Güncellenemedi",
      );
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6 px-1 sm:px-0">
      <div>
        <Link
          to="/app/dashboard"
          data-testid="tasks-hub-back-link"
          className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-zinc-500 hover:text-zinc-700"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Dashboard
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
              Görevlerim
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Sana atanmış görevler — tarih ve projeye göre gruplu, tek bakışta karar ver.
            </p>
          </div>
        </div>
      </div>

      {/* Search + Project/Group filter row. Layout:
          - Search input keeps its own row on all sizes.
          - The two multi-select filters sit to the right of it on md+
            and drop below on mobile so they stay tap-friendly. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full sm:max-w-xl">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
            strokeWidth={2}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && searchQuery) {
                e.preventDefault();
                setSearchQuery("");
              }
            }}
            data-testid="tasks-hub-search"
            placeholder="Görev, açıklama veya etiket ara…"
            className="h-10 w-full rounded-full border border-zinc-200 bg-white pl-9 pr-24 text-sm text-zinc-900 placeholder-zinc-400 shadow-sm transition-colors focus:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-200"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="tasks-hub-search-clear"
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                title="Temizle (Esc)"
                aria-label="Aramayı temizle"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            ) : (
              <kbd className="hidden select-none rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-500 sm:inline-block">
                ⌘K
              </kbd>
            )}
          </div>
        </div>

        {/* Project + Group multi-selects */}
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <MultiSelectFilter
            icon={FolderKanban}
            label="Proje"
            items={projects.map((p) => ({
              id: p.id,
              label: p.name,
              color: p.color,
            }))}
            selected={projectFilter}
            onChange={(next) => {
              setProjectFilter(next);
              // Trim group filter to still-valid lists after project change.
              if (next.length > 0) {
                const allowed = new Set(
                  lists.filter((l) => next.includes(l.project_id)).map((l) => l.id),
                );
                setListFilter((prev) => prev.filter((id) => allowed.has(id)));
              }
            }}
            testId="tasks-hub-project-filter"
          />
          <MultiSelectFilter
            icon={Layers}
            label="Grup"
            items={
              (projectFilter.length > 0
                ? lists.filter((l) => projectFilter.includes(l.project_id))
                : lists
              ).map((l) => ({
                id: l.id,
                label: l.name,
                sub: projectMap[l.project_id]?.name,
                color: l.color,
              }))
            }
            selected={listFilter}
            onChange={setListFilter}
            testId="tasks-hub-list-filter"
          />
          {(projectFilter.length > 0 || listFilter.length > 0) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setProjectFilter([]);
                setListFilter([]);
              }}
              data-testid="tasks-hub-filters-clear"
              className="h-9 gap-1 rounded-full text-xs text-zinc-500 hover:text-zinc-800"
            >
              <X className="h-3 w-3" />
              Temizle
            </Button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="tasks-hub-filters"
      >
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const active = activeFilterKey === f.key;
          const count = tasks.filter(f.match).filter(searchMatches).length;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`tasks-hub-filter-${f.key}`}
              className={[
                "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
                active
                  ? "bg-zinc-900 text-white"
                  : "bg-white text-zinc-700 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-50",
              ].join(" ")}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              <span>{f.label}</span>
              <span
                className={[
                  "inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold",
                  active ? "bg-white text-zinc-900" : "bg-zinc-100 text-zinc-600",
                ].join(" ")}
              >
                {count}
              </span>
            </button>
          );
        })}
        <div className="ml-auto">
          <Button
            type="button"
            onClick={() => setComposerOpen((v) => !v)}
            data-testid="tasks-hub-composer-toggle"
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            size="sm"
          >
            <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={2.5} />
            {composerOpen ? "Ekleyiciyi kapat" : "Yeni görev"}
          </Button>
        </div>
      </div>

      {/* Inline quick-add composer — dropdown for project/group, plain input
          for the title so the form stays inline on wide screens and stacks
          on narrow ones. Enter submits. */}
      {composerOpen && (
        <form
          data-testid="tasks-hub-composer"
          onSubmit={(e) => {
            e.preventDefault();
            createInlineTask();
          }}
          className="flex flex-col gap-2 rounded-lg border border-zinc-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center"
        >
          <Select
            value={newProjectId || undefined}
            onValueChange={(v) => setNewProjectId(v)}
            disabled={creating || projects.length === 0}
          >
            <SelectTrigger
              className="w-full sm:w-[180px]"
              data-testid="tasks-hub-composer-project"
            >
              <SelectValue placeholder="Proje" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={newListId || undefined}
            onValueChange={(v) => setNewListId(v)}
            disabled={creating || listsForNewProject.length === 0}
          >
            <SelectTrigger
              className="w-full sm:w-[180px]"
              data-testid="tasks-hub-composer-list"
            >
              <SelectValue
                placeholder={
                  listsForNewProject.length === 0 ? "Bu projede grup yok" : "Grup"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {listsForNewProject.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.emoji ? `${l.emoji} ` : ""}
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            data-testid="tasks-hub-composer-title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Görev başlığı… (Enter ile ekle)"
            maxLength={200}
            disabled={creating}
            className="flex-1"
            autoFocus
          />
          <Input
            data-testid="tasks-hub-composer-due"
            type="date"
            value={newDueAt}
            onChange={(e) => setNewDueAt(e.target.value)}
            className="w-full sm:w-[150px]"
            disabled={creating}
            title="Vade tarihi (opsiyonel)"
          />
          <Button
            type="submit"
            data-testid="tasks-hub-composer-submit"
            disabled={
              creating || !newTitle.trim() || !newProjectId || !newListId
            }
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {creating ? "Ekleniyor…" : "Ekle"}
          </Button>
        </form>
      )}

      {loading && (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
          Yükleniyor…
        </div>
      )}

      {!loading && totalVisible === 0 && (
        <div
          data-testid="tasks-hub-empty"
          className="rounded-lg border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500"
        >
          {trimmedQuery ? (
            <span>
              &ldquo;<span className="font-semibold text-zinc-700">{trimmedQuery}</span>&rdquo;
              için sonuç yok.{" "}
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="font-semibold text-zinc-900 underline hover:no-underline"
              >
                Aramayı temizle
              </button>
            </span>
          ) : (
            "Bu filtrede görev yok. 🎉"
          )}
        </div>
      )}

      {!loading &&
        BUCKET_ORDER.map((bucket) => {
          const rows = buckets[bucket] || [];
          if (rows.length === 0) return null;
          const isCollapsed = !!collapsed[bucket];
          return (
            <section
              key={bucket}
              data-testid={`tasks-hub-bucket-${bucket}`}
              className="space-y-2"
            >
              <button
                type="button"
                onClick={() => toggleBucket(bucket)}
                className="flex w-full items-center gap-2 rounded-md py-1 text-left"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-zinc-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-zinc-400" />
                )}
                <span
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
                    BUCKET_TONE[bucket],
                  ].join(" ")}
                >
                  {BUCKET_LABELS[bucket]}
                </span>
                <span className="text-xs font-semibold text-zinc-500">
                  {rows.length}
                </span>
              </button>
              {!isCollapsed && (
                <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
                  {/* ─── Desktop table (md and above) ─── */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[680px] text-sm">
                      <thead className="border-b border-zinc-200 bg-zinc-50/70 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="w-[38%] px-3 py-2 text-left">Görev</th>
                          <th className="w-[20%] px-3 py-2 text-left">Proje</th>
                          <th className="w-[15%] px-3 py-2 text-left">Grup</th>
                          <th className="w-[13%] px-3 py-2 text-left">Vade</th>
                          <th className="w-[7%] px-3 py-2 text-left">Öncelik</th>
                          <th className="w-[7%] px-3 py-2 text-left">Kişi</th>
                        </tr>
                      </thead>
                    <tbody>
                      {rows.map((task) => {
                        const proj = projectMap[task.project_id];
                        const list = listMap[task.list_id];
                        const assignees = (
                          task.assignee_ids?.length
                            ? task.assignee_ids
                            : task.assignee_id
                              ? [task.assignee_id]
                              : []
                        )
                          .map((id) => userMap[id])
                          .filter(Boolean);
                        const badge = dueBadge(
                          task.due_at,
                          tenant?.timezone,
                          task.status,
                        );
                        const listPalette = getListPalette(list?.color);
                        return (
                          <tr
                            key={task.id}
                            onClick={() => openTask(task)}
                            data-testid={`tasks-hub-row-${task.id}`}
                            className={[
                              "cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50",
                              task.status === "done" ? "opacity-60" : "",
                            ].join(" ")}
                          >
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const canEdit = canEditProtectedTaskFields(
                                    currentUser,
                                    task,
                                  );
                                  const btn = (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!canEdit) return;
                                        handleToggleDone(task);
                                      }}
                                      disabled={
                                        !canEdit || togglingIds.has(task.id)
                                      }
                                      aria-label={
                                        task.status === "done"
                                          ? "Görevi tekrar aç"
                                          : "Görevi tamamlandı olarak işaretle"
                                      }
                                      data-testid={`tasks-hub-toggle-${task.id}`}
                                      className={[
                                        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                        task.status === "done"
                                          ? "border-emerald-500 bg-emerald-500 text-white hover:border-emerald-600 hover:bg-emerald-600"
                                          : "border-zinc-300 bg-white text-transparent hover:border-emerald-400 hover:text-emerald-400",
                                        togglingIds.has(task.id) ? "opacity-50" : "",
                                        !canEdit ? "cursor-not-allowed opacity-40 hover:border-zinc-300 hover:text-transparent" : "",
                                      ].join(" ")}
                                    >
                                      <Check className="h-3 w-3" strokeWidth={3} />
                                    </button>
                                  );
                                  return (
                                    <LockedTaskField
                                      locked={!canEdit}
                                      reason="complete"
                                      showBadge={!canEdit}
                                      taskId={task.id}
                                    >
                                      {btn}
                                    </LockedTaskField>
                                  );
                                })()}
                                <span
                                  className={
                                    task.status === "done"
                                      ? "line-through text-zinc-500"
                                      : "text-zinc-900 font-medium"
                                  }
                                >
                                  {task.visibility &&
                                    task.visibility !== "everyone" && (
                                      <Lock
                                        className="mr-1 inline h-3 w-3 -translate-y-px text-amber-500"
                                        data-testid={`task-private-${task.id}`}
                                      />
                                    )}
                                  {task.title}
                                </span>
                                {/* Label chips — max 3 shown inline, rest folded into "+N". */}
                                {task.label_ids && task.label_ids.length > 0 && (
                                  <span className="inline-flex flex-wrap items-center gap-1">
                                    {task.label_ids
                                      .slice(0, 3)
                                      .map((lid) => {
                                        const lbl = labelMap[lid];
                                        if (!lbl) return null;
                                        const p = getListPalette(lbl.color);
                                        return (
                                          <span
                                            key={lid}
                                            data-testid={`tasks-hub-row-label-${task.id}-${lid}`}
                                            className={[
                                              "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                              lbl.color ? p.chip : "bg-zinc-100 text-zinc-700",
                                            ].join(" ")}
                                            title={lbl.name}
                                          >
                                            {lbl.emoji ? (
                                              <span aria-hidden>{lbl.emoji}</span>
                                            ) : null}
                                            <span>{lbl.name}</span>
                                          </span>
                                        );
                                      })}
                                    {task.label_ids.length > 3 && (
                                      <span
                                        className="inline-flex items-center rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600"
                                        title={`+${task.label_ids.length - 3} etiket daha`}
                                      >
                                        +{task.label_ids.length - 3}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              {proj ? (
                                <Link
                                  to={`/app/projects/${task.project_id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs font-medium text-violet-700 hover:underline"
                                >
                                  {proj.name}
                                </Link>
                              ) : (
                                <span className="text-xs italic text-zinc-400">
                                  Silinmiş proje
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              {list ? (
                                <span
                                  className={[
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                                    list.color ? listPalette.chip : "bg-zinc-100 text-zinc-700",
                                  ].join(" ")}
                                >
                                  {list.emoji ? (
                                    <span aria-hidden>{list.emoji}</span>
                                  ) : null}
                                  <span>{list.name}</span>
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              {badge ? (
                                <span
                                  className={[
                                    "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium",
                                    badge.tone === "red"
                                      ? "bg-red-50 text-red-700"
                                      : badge.tone === "amber"
                                        ? "bg-amber-50 text-amber-700"
                                        : badge.tone === "blue"
                                          ? "bg-sky-50 text-sky-700"
                                          : "bg-zinc-100 text-zinc-600",
                                  ].join(" ")}
                                >
                                  {badge.dateStr ? `${badge.dateStr}` : ""}
                                  {badge.dateStr && badge.timePart ? " · " : ""}
                                  {badge.timePart}
                                  {badge.daysAgo ? ` (${badge.daysAgo}g)` : ""}
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <span
                                className={[
                                  "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                                  PRIORITY_TONE[task.priority] || PRIORITY_TONE.medium,
                                ].join(" ")}
                              >
                                {task.priority?.slice(0, 3) || "MED"}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              {assignees.length ? (
                                <span
                                  className="flex -space-x-1.5"
                                  title={assignees.map((a) => a.name).join(", ")}
                                >
                                  {assignees.slice(0, 3).map((a) => (
                                    <span
                                      key={a.id}
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-700 ring-2 ring-white"
                                    >
                                      {initials(a.name)}
                                    </span>
                                  ))}
                                  {assignees.length > 3 && (
                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-[10px] font-bold text-zinc-600 ring-2 ring-white">
                                      +{assignees.length - 3}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <User2 className="h-4 w-4 text-zinc-300" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    </table>
                  </div>

                  {/* ─── Mobile card list (below md breakpoint) ─── */}
                  <ul className="divide-y divide-zinc-100 md:hidden">
                    {rows.map((task) => {
                      const proj = projectMap[task.project_id];
                      const list = listMap[task.list_id];
                      const assignees = (
                        task.assignee_ids?.length
                          ? task.assignee_ids
                          : task.assignee_id
                            ? [task.assignee_id]
                            : []
                      )
                        .map((id) => userMap[id])
                        .filter(Boolean);
                      const badge = dueBadge(
                        task.due_at,
                        tenant?.timezone,
                        task.status,
                      );
                      const listPalette = getListPalette(list?.color);
                      const canEdit = canEditProtectedTaskFields(
                        currentUser,
                        task,
                      );
                      return (
                        <li
                          key={task.id}
                          onClick={() => openTask(task)}
                          data-testid={`tasks-hub-row-${task.id}`}
                          className={[
                            "flex cursor-pointer flex-col gap-2 px-3 py-3 transition-colors active:bg-zinc-100",
                            task.status === "done" ? "opacity-60" : "",
                          ].join(" ")}
                        >
                          {/* Row 1 — checkbox + title */}
                          <div className="flex items-start gap-2.5">
                            <div
                              className="pt-0.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <LockedTaskField
                                locked={!canEdit}
                                reason="complete"
                                showBadge={!canEdit}
                                taskId={task.id}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!canEdit) return;
                                    handleToggleDone(task);
                                  }}
                                  disabled={
                                    !canEdit || togglingIds.has(task.id)
                                  }
                                  aria-label={
                                    task.status === "done"
                                      ? "Görevi tekrar aç"
                                      : "Görevi tamamlandı olarak işaretle"
                                  }
                                  data-testid={`tasks-hub-toggle-${task.id}`}
                                  className={[
                                    "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                    task.status === "done"
                                      ? "border-emerald-500 bg-emerald-500 text-white"
                                      : "border-zinc-300 bg-white text-transparent",
                                    togglingIds.has(task.id) ? "opacity-50" : "",
                                    !canEdit ? "cursor-not-allowed opacity-40" : "",
                                  ].join(" ")}
                                >
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                </button>
                              </LockedTaskField>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div
                                className={[
                                  "break-words text-[14px] leading-snug",
                                  task.status === "done"
                                    ? "line-through text-zinc-500"
                                    : "text-zinc-900 font-medium",
                                ].join(" ")}
                              >
                                {task.visibility &&
                                  task.visibility !== "everyone" && (
                                    <Lock
                                      className="mr-1 inline h-3 w-3 -translate-y-px text-amber-500"
                                      data-testid={`task-private-mobile-${task.id}`}
                                    />
                                  )}
                                {task.title}
                              </div>
                              {task.label_ids && task.label_ids.length > 0 && (
                                <div className="mt-1 flex flex-wrap items-center gap-1">
                                  {task.label_ids.slice(0, 3).map((lid) => {
                                    const lbl = labelMap[lid];
                                    if (!lbl) return null;
                                    const p = getListPalette(lbl.color);
                                    return (
                                      <span
                                        key={lid}
                                        className={[
                                          "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                          lbl.color
                                            ? p.chip
                                            : "bg-zinc-100 text-zinc-700",
                                        ].join(" ")}
                                      >
                                        {lbl.emoji ? (
                                          <span aria-hidden>{lbl.emoji}</span>
                                        ) : null}
                                        <span>{lbl.name}</span>
                                      </span>
                                    );
                                  })}
                                  {task.label_ids.length > 3 && (
                                    <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
                                      +{task.label_ids.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Priority chip aligned right */}
                            <span
                              className={[
                                "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider",
                                PRIORITY_TONE[task.priority] ||
                                  PRIORITY_TONE.medium,
                              ].join(" ")}
                            >
                              {task.priority?.slice(0, 3) || "MED"}
                            </span>
                          </div>

                          {/* Row 2 — metadata line: project · list · due · assignee */}
                          <div className="flex flex-wrap items-center gap-1.5 pl-7 text-[11px]">
                            {proj ? (
                              <Link
                                to={`/app/projects/${task.project_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-violet-700 hover:underline"
                              >
                                {proj.name}
                              </Link>
                            ) : (
                              <span className="italic text-zinc-400">
                                Silinmiş proje
                              </span>
                            )}
                            {list && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span
                                  className={[
                                    "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                    list.color
                                      ? listPalette.chip
                                      : "bg-zinc-100 text-zinc-700",
                                  ].join(" ")}
                                >
                                  {list.emoji ? (
                                    <span aria-hidden>{list.emoji}</span>
                                  ) : null}
                                  <span>{list.name}</span>
                                </span>
                              </>
                            )}
                            {badge && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span
                                  className={[
                                    "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-medium",
                                    badge.tone === "red"
                                      ? "bg-red-50 text-red-700"
                                      : badge.tone === "amber"
                                        ? "bg-amber-50 text-amber-700"
                                        : badge.tone === "blue"
                                          ? "bg-sky-50 text-sky-700"
                                          : "bg-zinc-100 text-zinc-600",
                                  ].join(" ")}
                                >
                                  {badge.dateStr || ""}
                                  {badge.dateStr && badge.timePart ? " · " : ""}
                                  {badge.timePart}
                                  {badge.daysAgo ? ` (${badge.daysAgo}g)` : ""}
                                </span>
                              </>
                            )}
                            <span className="ml-auto shrink-0">
                              {assignees.length ? (
                                <span
                                  className="flex -space-x-1"
                                  title={assignees.map((a) => a.name).join(", ")}
                                >
                                  {assignees.slice(0, 3).map((a) => (
                                    <span
                                      key={a.id}
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-[9px] font-bold text-violet-700 ring-1 ring-white"
                                    >
                                      {initials(a.name)}
                                    </span>
                                  ))}
                                  {assignees.length > 3 && (
                                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-[9px] font-bold text-zinc-600 ring-1 ring-white">
                                      +{assignees.length - 3}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <User2 className="h-3.5 w-3.5 text-zinc-300" />
                              )}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>
          );
        })}

      {panelTask && (
        <TaskRightPanel
          task={panelTask}
          users={users}
          lists={lists}
          onClose={() => setPanelTask(null)}
          onExpand={() => {
            setDialogTask(panelTask);
            setPanelTask(null);
          }}
          onSave={async (patch) => {
            await handleUpdate(panelTask.id, patch);
            setPanelTask((prev) => (prev ? { ...prev, ...patch } : prev));
          }}
          onDelete={async () => {
            await handleDelete(panelTask.id);
            setPanelTask(null);
            toast.success("Görev silindi");
          }}
          tenantTz={tenant?.timezone}
        />
      )}

      {dialogTask && (
        <TaskDialog
          task={dialogTask}
          users={users}
          onClose={() => setDialogTask(null)}
          onSave={async (patch) => {
            await handleUpdate(dialogTask.id, patch);
            setDialogTask((prev) => (prev ? { ...prev, ...patch } : prev));
            toast.success("Güncellendi");
          }}
          onDelete={async () => {
            await handleDelete(dialogTask.id);
            setDialogTask(null);
            toast.success("Görev silindi");
          }}
          tenantTz={tenant?.timezone}
        />
      )}
    </div>
  );
}


/**
 * MultiSelectFilter — chip-style trigger that opens a Popover with a
 * scroll-able checklist. Used for Project + Group filters on TasksHub.
 *
 * Props
 *   icon      Lucide component drawn on the left of the pill.
 *   label     Placeholder shown when nothing is selected (e.g. "Proje").
 *   items     [{ id, label, sub?, color? }]
 *   selected  string[] — controlled selection state.
 *   onChange  (next: string[]) => void
 *   testId    stable data-testid prefix.
 */
function MultiSelectFilter({ icon: Icon, label, items, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredItems = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return items;
    return items.filter(
      (it) =>
        it.label?.toLocaleLowerCase("tr").includes(q) ||
        (it.sub || "").toLocaleLowerCase("tr").includes(q),
    );
  }, [items, query]);

  const toggle = (id) => {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id],
    );
  };

  const count = selected.length;
  const summary =
    count === 0
      ? label
      : count === 1
        ? items.find((i) => i.id === selected[0])?.label || `1 ${label}`
        : `${count} ${label}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={testId}
          className={[
            "h-9 gap-1.5 rounded-full border-zinc-200 px-3 text-xs font-medium",
            count > 0
              ? "border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
              : "text-zinc-700 hover:bg-zinc-50",
          ].join(" ")}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="max-w-[140px] truncate">{summary}</span>
          {count > 0 && (
            <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-violet-600 px-1 text-[9px] font-bold text-white">
              {count}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0"
        data-testid={`${testId}-popover`}
        // Prevent Radix from auto-focusing the search input on open. On
        // mobile this would pop the on-screen keyboard immediately and
        // cover the checklist, making project/group selection impossible.
        // Users can still tap the search input to filter.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-zinc-100 p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`${label} ara…`}
              data-testid={`${testId}-search`}
              className="h-8 w-full rounded-md border border-zinc-200 bg-white pl-7 pr-2 text-xs placeholder-zinc-400 focus:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-200"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filteredItems.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-zinc-400">
              Sonuç yok
            </div>
          ) : (
            filteredItems.map((it) => {
              const checked = selected.includes(it.id);
              return (
                <label
                  key={it.id}
                  data-testid={`${testId}-item-${it.id}`}
                  className={[
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs",
                    checked ? "bg-violet-50" : "hover:bg-zinc-50",
                  ].join(" ")}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(it.id)}
                  />
                  {it.color && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: it.color }}
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="truncate font-medium text-zinc-800">
                      {it.label}
                    </span>
                    {it.sub && (
                      <span className="ml-1 text-[10px] text-zinc-400">
                        · {it.sub}
                      </span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>
        {count > 0 && (
          <div className="border-t border-zinc-100 p-1">
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}
              className="w-full rounded-md px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            >
              Seçimleri temizle
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
